import React, { useState, useEffect } from 'react';
import { SensorData } from '../types';
import { fetchHourlyConsumption, fetchDailyConsumption, fetchMonthlyConsumption, fetchLastHourTotal, fetchAnalytics, ConsumptionPoint, SensorAnalytics } from '../services/dataService';
import { Droplets, ChevronDown } from 'lucide-react';
import { ResponsiveContainer, ComposedChart, Bar, Line, Cell, XAxis, YAxis, Tooltip } from 'recharts';

interface M3TRmonCardProps {
  data: SensorData;
  // Bumped by the dashboard's refresh button. The card fetches its own series,
  // keyed on the sensor id — which doesn't change on a refresh, so without
  // this the charts kept whatever they loaded on mount and only a full page
  // reload moved them.
  refreshKey?: number;
  // Day of month the bill closes. Without it there is no period to project
  // over, so the projection simply isn't shown.
  billingCutoffDay?: number | null;
}


const findValue = (data: SensorData, key: string) => data.values.find(v => v.key === key);


// Charts are the detail, the totals are the answer. On a phone three charts per
// sensor push everything else off the screen, so they start closed.
const Section: React.FC<{ title: React.ReactNode; badge?: React.ReactNode; defaultOpen?: boolean; children: React.ReactNode }> = ({ title, badge, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-slate-700/50 pt-3">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between text-xs text-slate-400 hover:text-slate-200"
      >
        <span className="flex items-center gap-2">{title}</span>
        <span className="flex items-center gap-2">{badge}<ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} /></span>
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
};


const BAR_COLOR = '#22d3ee';
const BAR_COLOR_GAP = '#0e7490';   // same cyan, darkened
const TEMP_COLOR = '#f59e0b';

// Recharts colours a tooltip entry from the series, not from the Cell that was
// actually hovered — so an amber incomplete bar still described itself in
// cyan. This reads the bar's own state instead.
const ChartTooltip: React.FC<any> = ({ active, payload, label, labelSuffix = '' }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const litres = payload.find((p: any) => p.dataKey === 'liters');
  const temp = payload.find((p: any) => p.dataKey === 'tempC');
  return (
    <div className="bg-slate-800 rounded-lg px-3 py-2 text-xs shadow-lg">
      <p className="text-slate-400 mb-1">{label}{labelSuffix}</p>
      {litres && (
        <p style={{ color: row.offline ? BAR_COLOR_GAP : BAR_COLOR }}>
          {litres.value} L{row.offline ? ' (incompleto)' : ''}
        </p>
      )}
      {temp && temp.value !== null && (
        <p style={{ color: TEMP_COLOR }}>{temp.value} °C</p>
      )}
    </div>
  );
};

const M3TRmonCard: React.FC<M3TRmonCardProps> = ({ data, refreshKey = 0, billingCutoffDay = null }) => {
  const [hourly, setHourly] = useState<ConsumptionPoint[]>([]);
  const [daily, setDaily] = useState<ConsumptionPoint[]>([]);
  const [monthly, setMonthly] = useState<ConsumptionPoint[]>([]);
  const [lastHour, setLastHour] = useState<number | null>(null);
  const [stats, setStats] = useState<SensorAnalytics | null>(null);

  useEffect(() => {
    fetchHourlyConsumption(data.id).then(setHourly);
    fetchDailyConsumption(data.id).then(setDaily);
    fetchMonthlyConsumption(data.id).then(setMonthly);
    fetchLastHourTotal(data.id).then(setLastHour);
    if (billingCutoffDay) fetchAnalytics(data.id, billingCutoffDay).then(setStats);
    else setStats(null);
  }, [data.id, refreshKey, billingCutoffDay]);

  // Only draw the temperature line where the device actually sends one.
  const anyTemp = (pts: ConsumptionPoint[]) =>
    pts.some(p => p.tempC !== null && p.tempC !== undefined);
  const hasTemp = anyTemp(hourly);
  const hasTempDaily = anyTemp(daily);
  const hasTempMonthly = anyTemp(monthly);

  const sum = (pts: ConsumptionPoint[]) =>
    pts.length === 0 ? null : Number(pts.reduce((t, p) => t + p.liters, 0).toFixed(1));
  const todayTotal = sum(hourly);
  const monthTotal = sum(daily);

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden shadow-lg">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-700/50 flex justify-between items-center bg-slate-800/50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-900 rounded-lg">
            <Droplets className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="font-medium text-slate-200">{data.name}</h3>
            <p className="text-xs text-slate-500">Actualizado: {data.lastSeen}</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-5">

        {/* Consumption totals. Rendered unconditionally — a zero is a real
            answer here, and hiding the card when a device hasn't reported the
            key it happens to use made two identical sensors look different. */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-900/50 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Última hora</p>
            <p className="text-xl font-bold text-slate-100">
              {lastHour === null ? '—' : lastHour} <span className="text-sm font-normal text-slate-500">L</span>
            </p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Consumo hoy</p>
            <p className="text-xl font-bold text-slate-100">
              {todayTotal === null ? '—' : todayTotal} <span className="text-sm font-normal text-slate-500">L</span>
            </p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Consumo mes</p>
            <p className="text-xl font-bold text-slate-100">
              {monthTotal === null ? '—' : monthTotal} <span className="text-sm font-normal text-slate-500">L</span>
            </p>
          </div>
        </div>


        <Section
          title="Gráficas de consumo"
        >
          <div className="space-y-5">
        {/* Today, hour by hour */}
          <div>
            <p className="text-xs text-slate-500 mb-2 flex items-center justify-between">
              <span>{<>Hoy — consumo por hora (L){hasTemp && <span className="text-amber-500"> · temperatura (°C)</span>}</>}</span>
            </p>
            <div className="h-36 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={hourly}>
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} interval={2} />
                  <YAxis yAxisId="l" tick={{ fontSize: 10, fill: '#64748b' }} width={44}
                         tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)} />
                  {hasTemp && (
                    // Its own axis: litres and degrees share no scale, and
                    // forcing them onto one flattens whichever range is smaller.
                    <YAxis yAxisId="t" orientation="right" width={38}
                           tick={{ fontSize: 10, fill: '#f59e0b' }}
                           domain={['dataMin - 2', 'dataMax + 2']} />
                  )}
                  <Tooltip content={<ChartTooltip labelSuffix=":00" />} cursor={{ fill: "#ffffff10" }} />
                  <Bar yAxisId="l" dataKey="liters" radius={[3, 3, 0, 0]}>
                    {hourly.map((h, i) => (
                      <Cell key={i} fill={h.offline ? BAR_COLOR_GAP : BAR_COLOR} />
                    ))}
                  </Bar>
                  {hasTemp && (
                    <Line yAxisId="t" type="monotone" dataKey="tempC" stroke={TEMP_COLOR}
                          strokeWidth={2} connectNulls={false}
                          dot={{ r: 2, fill: TEMP_COLOR, strokeWidth: 0 }} />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>


        {/* Daily consumption — current month */}
          <div>
            <p className="text-xs text-slate-500 mb-2 flex items-center justify-between">
              <span>Este mes — consumo por día (L)</span>
            </p>
            <div className="h-32 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={daily}>
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} interval={2} />
                  <YAxis yAxisId="l" tick={{ fontSize: 10, fill: '#64748b' }} width={44}
                         tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)} />
                  {hasTempDaily && (
                    <YAxis yAxisId="t" orientation="right" width={38}
                           tick={{ fontSize: 10, fill: '#f59e0b' }}
                           domain={['dataMin - 2', 'dataMax + 2']} />
                  )}
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "#ffffff10" }} />
                  <Bar yAxisId="l" dataKey="liters" radius={[3, 3, 0, 0]}>
                    {daily.map((d, i) => (
                      <Cell key={i} fill={d.offline ? BAR_COLOR_GAP : BAR_COLOR} />
                    ))}
                  </Bar>
                  {hasTempDaily && (
                    <Line yAxisId="t" type="monotone" dataKey="tempC" stroke={TEMP_COLOR}
                          strokeWidth={2} connectNulls={false}
                          dot={{ r: 2, fill: TEMP_COLOR, strokeWidth: 0 }} />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>


        {/* Monthly consumption — current year */}
          <div>
            <p className="text-xs text-slate-500 mb-2 flex items-center justify-between">
              <span>Este año — consumo por mes (L)</span>
            </p>
            <div className="h-32 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={monthly}>
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} />
                  <YAxis yAxisId="l" tick={{ fontSize: 10, fill: '#64748b' }} width={44}
                         tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)} />
                  {hasTempMonthly && (
                    <YAxis yAxisId="t" orientation="right" width={38}
                           tick={{ fontSize: 10, fill: '#f59e0b' }}
                           domain={['dataMin - 2', 'dataMax + 2']} />
                  )}
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "#ffffff10" }} />
                  <Bar yAxisId="l" dataKey="liters" radius={[3, 3, 0, 0]}>
                    {monthly.map((m, i) => (
                      <Cell key={i} fill={m.offline ? BAR_COLOR_GAP : BAR_COLOR} />
                    ))}
                  </Bar>
                  {hasTempMonthly && (
                    <Line yAxisId="t" type="monotone" dataKey="tempC" stroke={TEMP_COLOR}
                          strokeWidth={2} connectNulls={false}
                          dot={{ r: 2, fill: TEMP_COLOR, strokeWidth: 0 }} />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
          {(hourly.some(x => x.offline) || daily.some(x => x.offline) || monthly.some(x => x.offline)) && (
            <p className="text-[11px] text-slate-500">Barras opacas significa que faltan datos.</p>
          )}
          </div>
        </Section>

        <Section title="Analíticos">
          <div className="space-y-4 text-sm">
            {!billingCutoffDay ? (
              <p className="text-slate-500">
                Define tu día de corte en Configuración para ver la proyección del periodo.
              </p>
            ) : !stats ? (
              <p className="text-slate-500">Sin datos suficientes todavía.</p>
            ) : (
              <>
                <div>
                  <p className="text-slate-400 mb-1">Proyección al corte</p>
                  <p className="text-2xl font-bold text-slate-100">
                    {stats.projected.toLocaleString()} <span className="text-base font-normal text-slate-500">L</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Llevas {stats.usedSoFar.toLocaleString()} L desde el{' '}
                    {stats.periodStart.toLocaleDateString()}, con un promedio de{' '}
                    {stats.dailyAverage.toLocaleString()} L por día. El periodo cierra el{' '}
                    {stats.periodEnd.toLocaleDateString()}.
                  </p>
                  {stats.completeDays < 7 && (
                    <p className="text-xs text-amber-500/80 mt-1">
                      Basada en {stats.completeDays}{' '}
                      {stats.completeDays === 1 ? 'día completo' : 'días completos'} — todavía poco
                      para ser confiable.
                    </p>
                  )}
                </div>

                <div className="border-t border-slate-700/50 pt-3">
                  <p className="text-slate-400 mb-1">Flujo máximo de hoy</p>
                  {stats.peakLpm === null || stats.peakLpm === 0 ? (
                    <p className="text-slate-500">Sin consumo registrado hoy.</p>
                  ) : (
                    <>
                      <p className="text-2xl font-bold text-slate-100">
                        {stats.peakLpm} <span className="text-base font-normal text-slate-500">L/min</span>
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        A las {stats.peakAt?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.
                        Es el caudal más alto reportado; una ráfaga corta dentro de un intervalo de
                        reporte queda promediada.
                      </p>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </Section>
      </div>
    </div>
  );
};

export default M3TRmonCard;
